import { forwardRef, useId } from 'react';

export interface RadioOption {
  value: string;
  label: string;
  description?: string;
  disabled?: boolean;
}

export interface RadioGroupProps {
  name: string;
  label?: string;
  options: RadioOption[];
  value?: string;
  onChange?: (value: string) => void;
  error?: string;
  hint?: string;
  required?: boolean;
  disabled?: boolean;
  orientation?: 'horizontal' | 'vertical';
  containerClassName?: string;
}

export const RadioGroup = forwardRef<HTMLDivElement, RadioGroupProps>(
  (
    {
      name,
      label,
      options,
      value,
      onChange,
      error,
      hint,
      required,
      disabled,
      orientation = 'vertical',
      containerClassName = '',
    },
    ref
  ) => {
    const generatedId = useId();
    const groupId = generatedId;

    const handleChange = (optionValue: string) => {
      if (!disabled) {
        onChange?.(optionValue);
      }
    };

    return (
      <div ref={ref} className={`mb-4 ${containerClassName}`} role="radiogroup" aria-labelledby={`${groupId}-label`} aria-required={required} aria-describedby={error ? `${groupId}-error` : hint ? `${groupId}-hint` : undefined}>
        {label && (
          <p id={`${groupId}-label`} className="block text-sm font-medium text-gray-700 mb-2">
            {label}
            {required && <span className="text-red-500 ml-1">*</span>}
          </p>
        )}

        <div
          className={`
            ${orientation === 'horizontal' ? 'flex flex-wrap gap-4' : 'space-y-3'}
          `}
        >
          {options.map((option) => {
            const optionId = `${groupId}-${option.value}`;
            const isSelected = value === option.value;
            const isDisabled = disabled || option.disabled;

            return (
              <div key={option.value} className="flex items-start">
                <div className="flex items-center h-5">
                  <input
                    id={optionId}
                    name={name}
                    type="radio"
                    value={option.value}
                    checked={isSelected}
                    onChange={() => handleChange(option.value)}
                    disabled={isDisabled}
                    className={`
                      w-5 h-5
                      border-2 rounded-full
                      text-green-600
                      focus:ring-2 focus:ring-green-200 focus:ring-offset-0
                      transition-all duration-200
                      cursor-pointer
                      disabled:cursor-not-allowed disabled:opacity-50
                      ${error ? 'border-red-500' : 'border-gray-300 hover:border-green-500'}
                    `}
                    aria-invalid={!!error}
                  />
                </div>
                <div className="ml-3">
                  <label
                    htmlFor={optionId}
                    className={`
                      text-sm font-medium cursor-pointer
                      ${isDisabled ? 'text-gray-400 cursor-not-allowed' : 'text-gray-700'}
                    `}
                  >
                    {option.label}
                  </label>
                  {option.description && (
                    <p className="text-sm text-gray-500 mt-0.5">{option.description}</p>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {error && (
          <p id={`${groupId}-error`} className="mt-2 text-sm text-red-600" role="alert">
            {error}
          </p>
        )}

        {hint && !error && <p id={`${groupId}-hint`} className="mt-2 text-sm text-gray-500">{hint}</p>}
      </div>
    );
  }
);

RadioGroup.displayName = 'RadioGroup';

export default RadioGroup;
